import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Navigation, Pagination } from "swiper/modules";
import userimg from "../../assets/user.png";
import useSponsoredAds from "../../hook/useSponsoredAds";
import SponsoredAdCard from "../ads/SponsoredAdCard";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const getToken = () => sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

// Phone / tablet version of the right sidebar: people to follow, your groups, and sponsored ads.
// It reads the same data as the desktop sidebar, so nothing here is placeholder.
// showSponsoredAds is opt-in (only the home feed is a documented "feed" ad placement).
export default function RightResponsivesidebar({ showSponsoredAds = false }) {
    const navigate = useNavigate();
    const [people, setPeople] = useState([]);
    const [peopleState, setPeopleState] = useState({ loading: true, error: "" });
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const { ads: sponsoredAds, trackEvent, viewRef, removeAd } = useSponsoredAds({
        placement: "feed",
        context: { page: "home" },
        count: 3,
        enabled: showSponsoredAds,
    });
    const requested = useRef(false);

    // People you may want to follow
    useEffect(() => {
        if (requested.current) return undefined;
        requested.current = true;
        const token = getToken();
        if (!token) {
            setPeopleState({ loading: false, error: "" });
            setGroupsLoading(false);
            return undefined;
        }
        let active = true;
        const headers = { token, "Content-Type": "application/json" };

        axios
            .post(`${API_BASE_URL}/user/get-all-users`, { filters: { isNotFriends: true }, page: 1, limit: 10 }, { headers })
            .then(({ data }) => {
                if (!active) return;
                if (data?.success) {
                    setPeople(data.result?.users || []);
                    setPeopleState({ loading: false, error: "" });
                } else {
                    setPeopleState({ loading: false, error: "We couldn't load suggestions." });
                }
            })
            .catch(() => active && setPeopleState({ loading: false, error: "We couldn't load suggestions." }));

        axios
            .post(`${API_BASE_URL}/group/get-groups`, { filters: { myAcceptedGroup: true }, page: 1, limit: 10 }, { headers })
            .then(({ data }) => {
                if (active && data?.success) setGroups((data.result?.groups || []).filter((g) => !g.is_deleted));
            })
            .catch(() => {})
            .finally(() => active && setGroupsLoading(false));

        return () => { active = false; };
    }, []);

    const toggleFollow = useCallback(async (person) => {
        const token = getToken();
        if (!token || busyId) return;
        const following = Boolean(person.isFriendRequestSent || person.friendshipStatus);
        if (following && !window.confirm("Are you sure you want to unfollow this user?")) return;
        setBusyId(person.id);
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/friend/add-friend`,
                { status: following ? "deleted" : "pending", user_id: person.id },
                { headers: { token, "Content-Type": "application/json" } }
            );
            if (data?.success) {
                setPeople((list) =>
                    list.map((p) => (p.id === person.id ? { ...p, isFriendRequestSent: !following, friendshipStatus: false } : p))
                );
            }
        } catch (e) {
            console.error("Could not update follow:", e);
        } finally {
            setBusyId(null);
        }
    }, [busyId]);

    const swiperProps = {
        spaceBetween: 10,
        slidesPerView: 2.2,
        breakpoints: { 480: { slidesPerView: 3 }, 768: { slidesPerView: 4 } },
        modules: [Navigation, Pagination],
    };

    return (
        <div className="slider-md-swiper">
            <aside className="bg-white p-3 lg:hidden min-w-0">
                {/* People to follow */}
                <h3 className="followers-title mb-4">Based on Your Followers</h3>
                {peopleState.loading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                ) : peopleState.error ? (
                    <p className="text-sm text-red-500">{peopleState.error}</p>
                ) : people.length === 0 ? (
                    <p className="text-sm text-gray-500">No suggestions right now.</p>
                ) : (
                    <Swiper {...swiperProps}>
                        {people.map((person) => {
                            const following = Boolean(person.isFriendRequestSent || person.friendshipStatus);
                            return (
                                <SwiperSlide key={person.id}>
                                    <div className="followers">
                                        <img
                                            src={person.profile_picture_url || userimg}
                                            alt={person.username || "User"}
                                            className="w-15 h-15 rounded-full cursor-pointer"
                                            onClick={() => navigate(`/myprofile/${person.id}`)}
                                        />
                                        <div>
                                            <p className="titlesemibold cursor-pointer" onClick={() => navigate(`/myprofile/${person.id}`)}>
                                                {`${person.first_name || ""} ${person.last_name || ""}`.trim() || person.username}
                                            </p>
                                            <p className="members">{Number(person.followersCount) || 0} followers</p>
                                        </div>
                                        <button
                                            type="button"
                                            className="follow-cta"
                                            disabled={busyId === person.id}
                                            onClick={() => toggleFollow(person)}
                                            style={following ? { background: "#9ca3af" } : undefined}
                                        >
                                            {busyId === person.id ? "..." : following ? "Unfollow" : "Follow"}
                                        </button>
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                )}

                <hr className="my-4" />

                {/* The groups you belong to */}
                <h3 className="followers-title mt-6 mb-4">Your Groups</h3>
                {groupsLoading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                ) : groups.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        You are not in any group yet.{" "}
                        <a href="/groupsfamily" className="font-semibold underline" style={{ color: "#095228" }}>Find or create one</a>
                    </p>
                ) : (
                    <Swiper {...swiperProps}>
                        {groups.map((group) => (
                            <SwiperSlide key={group.id}>
                                <div className="followers">
                                    <img src={group.group_image || userimg} alt={group.group_name || "Group"} className="w-15 h-15 rounded-full" />
                                    <div>
                                        <p className="titlesemibold">{group.group_name}</p>
                                        <p className="members">{Number(group.acceptedMembersCount) || 0} members</p>
                                    </div>
                                    <button type="button" className="follow-cta" onClick={() => navigate("/groupsfamily")}>Open</button>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}

                {/* Sponsored ads (same ads the desktop sidebar shows) */}
                {sponsoredAds.length > 0 && (
                    <>
                        <hr className="my-4" />
                        <h3 className="followers-title mb-4">Sponsored</h3>
                        {sponsoredAds.map((ad) => (
                            <SponsoredAdCard
                                key={ad.trackingId}
                                ad={ad}
                                onClick={trackEvent}
                                viewRef={viewRef(ad)}
                                onHidden={() => removeAd(ad.trackingId)}
                            />
                        ))}
                    </>
                )}
            </aside>
        </div>
    );
}
